import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useOutletContext, Link } from "react-router-dom";
import { getServices } from "../../Api/Api";
import acHeroImg from "../../Assets/ogac.jpg";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Typography,
  Box,
} from "@mui/material";

const serviceColors = [
  { surface: "bg-[#fff4e5]", border: "border-[#f4d3a4]" },
  { surface: "bg-[#eaf7f3]", border: "border-[#b9e1d4]" },
  { surface: "bg-[#eef2ff]", border: "border-[#cbd5ff]" },
  { surface: "bg-[#fff0f2]", border: "border-[#f3c4ce]" },
];

const ServiceDetails = () => {
  const { subcategory_id } = useParams();
  const navigate = useNavigate();
  const { onAddToCart } = useOutletContext();

  const [services, setServices] = useState([]);
  const [cartItems, setCartItems] = useState({});
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState("none");
  const [variantPrices, setVariantPrices] = useState({});

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const allServices = await getServices();
        const filtered = allServices.filter(
          (srv) =>
            String(srv.subcategory_id) === subcategory_id &&
            Number(srv.is_active) === 1
        );
        setServices(filtered);
      } catch (error) {
        console.error("Failed to load services", error);
      }
    };

    fetchServices();
  }, [subcategory_id]);

  const handleIncrease = (item) => {
    const currentQty = cartItems[item.id]?.quantity || 0;
    const updatedCart = {
      ...cartItems,
      [item.id]: { ...item, quantity: currentQty + 1 },
    };
    setCartItems(updatedCart);
    onAddToCart({ ...item, quantity: currentQty + 1 });
  };

  const handleDecrease = (item) => {
    const currentQty = cartItems[item.id]?.quantity || 0;
    if (currentQty <= 1) {
      const updated = { ...cartItems };
      delete updated[item.id];
      setCartItems(updated);
    } else {
      setCartItems({
        ...cartItems,
        [item.id]: { ...item, quantity: currentQty - 1 },
      });
    }
  };

  const handleRemove = (id) => {
    const updated = { ...cartItems };
    delete updated[id];
    setCartItems(updated);
  };

  const getQty = (id) => cartItems[id]?.quantity || 0;
  const cartList = Object.values(cartItems);
  const totalAmount = cartList.reduce(
    (acc, item) => acc + (Number(item.price) || 0) * (Number(item.quantity) || 1),
    0
  );

  const hasVariants = (srv) => {
    return (
      Number(srv.classicPrice) > 0 ||
      Number(srv.standardPrice) > 0 ||
      Number(srv.primePrice) > 0
    );
  };

  const categoryTitle =
    services.length > 0 && services[0].subcategory_name
      ? services[0].subcategory_name
      : "AC Repair & Servicing";

  return (
    <div className="min-h-screen bg-white">
      {/* 1. TOP HERO HEADER SECTION */}
      <div className="mb-10 border-b border-gray-100 bg-white px-4 py-12 sm:px-6 lg:px-12">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          {/* Left: Breadcrumbs & Title */}
          <div className="flex-1 space-y-3">
            <nav className="text-xs text-gray-500 font-medium flex items-center gap-2">
              <Link to="/" className="hover:text-black">Home</Link>
              <span>›</span>
              <span className="hover:text-black">Services</span>
              <span>›</span>
              <span className="text-black font-bold">{categoryTitle}</span>
            </nav>

            <h1 className="text-3xl font-semibold tracking-[-0.03em] text-[#0D004C] sm:text-4xl lg:text-5xl">
              Professional {categoryTitle}
            </h1>

            <p className="text-xs sm:text-sm text-gray-500 max-w-xl leading-relaxed">
              Keep your home cool and comfortable with our expert air conditioning maintenance, repair, and installation services.
            </p>
          </div>

          {/* Right: Top Hero Image */}
          <div className="w-full sm:w-[420px] h-[220px] sm:h-[260px] rounded-3xl overflow-hidden shadow-sm border border-gray-200/70 shrink-0">
            <img
              src={
                services[0]?.image
                  ? services[0].image.startsWith("http")
                    ? services[0].image
                    : `http://skishore.in/api/public/${services[0].image}`
                  : acHeroImg
              }
              alt="Professional Service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>

      {/* 2. TWO-COLUMN MAIN CONTENT (PIXEL-NEAT MATCHING SCREENSHOT) */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-3 gap-8 pb-16">
        
        {/* LEFT COLUMN: SERVICE CARDS (2/3 width) */}
        <div className="lg:col-span-2 space-y-6">
          {services.length === 0 ? (
            <div className="rounded-3xl border border-gray-100 bg-white p-10 text-center shadow-[0_4px_24px_rgba(0,0,0,0.03)]">
              <p className="text-sm font-semibold text-gray-500">No active services listed under this category yet.</p>
            </div>
          ) : (
            services.map((srv, index) => {
              const qty = getQty(srv.id);
              const color = serviceColors[index % serviceColors.length];

              return (
                <div
                  key={srv.id}
                  className={`group flex flex-col overflow-hidden rounded-2xl border ${color.border} bg-white shadow-[0_4px_20px_rgba(13,0,76,0.035)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(13,0,76,0.08)] sm:flex-row`}
                >
                  {/* Service Image (Left) */}
                  <div className={`h-52 w-full shrink-0 overflow-hidden sm:h-auto sm:w-[240px] ${color.surface}`}>
                    <img
                      src={
                        srv.image?.startsWith("http")
                          ? srv.image
                          : `http://skishore.in/api/public/${srv.image}`
                      }
                      alt={srv.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </div>

                  {/* Service Details (Right) */}
                  <div className="p-6 sm:p-7 flex flex-col justify-between flex-1 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold tracking-tight text-[#0D004C]">{srv.name}</h3>
                      <p className="text-xs sm:text-sm text-gray-500 mt-2 leading-relaxed line-clamp-2">
                        {srv.description ||
                          "Comprehensive cleaning of indoor and outdoor units, checking refrigerant levels, and ensuring optimal cooling performance."}
                      </p>
                    </div>

                    {/* Divider & Bottom Row */}
                    <div>
                      <div className="border-t border-gray-100 my-3" />
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs sm:text-sm font-bold text-gray-900">
                          {hasVariants(srv)
                            ? `Starts at ₹${srv.price}`
                            : Number(srv.price) < 100
                            ? `₹${srv.price} Consultation`
                            : `₹${srv.price}`}
                        </span>

                        <div>
                          {hasVariants(srv) ? (
                            <button
                              onClick={() => {
                                setSelectedService(srv);
                                setVariantPrices({
                                  classic: srv.classicPrice,
                                  standard: srv.standardPrice,
                                  premium: srv.primePrice,
                                });
                                setOpenDialog(true);
                              }}
                              className="rounded-xl bg-[#0D004C] px-6 py-2.5 text-xs font-bold text-white shadow-sm transition-all hover:bg-[#1a1460]"
                            >
                              View Packages
                            </button>
                          ) : qty === 0 ? (
                            <button
                              onClick={() =>
                                handleIncrease({
                                  ...srv,
                                  price: Number(srv.price),
                                  variant: "default",
                                })
                              }
                              className="rounded-xl border border-[#0D004C] px-6 py-2.5 text-xs font-bold text-[#0D004C] shadow-sm transition-all hover:bg-[#0D004C] hover:text-white"
                            >
                              Add to Cart
                            </button>
                          ) : (
                            <div className="flex items-center gap-4 rounded-xl bg-[#0D004C] px-4 py-2 text-xs font-bold text-white shadow-sm">
                              <button onClick={() => handleDecrease(srv)} className="text-base leading-none hover:text-red-400">
                                −
                              </button>
                              <span>{qty}</span>
                              <button onClick={() => handleIncrease(srv)} className="text-base leading-none hover:text-green-400">
                                +
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* RIGHT COLUMN: STICKY YOUR CART BOX (1/3 width matching screenshot) */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 h-max space-y-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-[0_8px_25px_rgba(13,0,76,0.06)] sm:rounded-3xl">
            <h3 className="flex items-center gap-2 text-lg font-bold tracking-tight text-[#0D004C]">
              🛒 Your Cart
            </h3>

            {cartList.length === 0 ? (
              <div className="text-center py-8 text-xs text-gray-400 font-medium">
                No services added to cart yet.
              </div>
            ) : (
              <div>
                <div className="space-y-4 divide-y divide-gray-100">
                  {cartList.map((item) => (
                    <div key={item.id} className="pt-3 first:pt-0 flex justify-between items-start gap-3">
                      <div>
                        <h4 className="font-bold text-xs text-gray-900">{item.name}</h4>
                        <p className="text-[11px] text-gray-400 font-normal mt-0.5 capitalize">
                          {item.variant === "none" ? "Classic Package" : `${item.variant} Package`}
                        </p>
                      </div>

                      <div className="text-right shrink-0">
                        <span className="text-xs font-bold text-gray-900">
                          ₹{(Number(item.price) * Number(item.quantity)).toLocaleString()}
                        </span>
                        <button
                          onClick={() => handleRemove(item.id)}
                          className="text-[10px] text-rose-500 font-semibold hover:underline block mt-0.5 text-right w-full"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-100 my-6 pt-6 flex justify-between items-end">
                  <span className="text-sm font-semibold text-gray-500">Subtotal</span>
                  <span className="text-2xl font-black text-gray-900">
                    ₹{totalAmount.toFixed(2)}
                  </span>
                </div>

                <button
                  onClick={() => navigate("/cart")}
                  className="mt-2 w-full rounded-xl bg-[#0D004C] py-3.5 text-xs font-bold tracking-wide text-white shadow-md transition-all hover:bg-[#1a1460]"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* FOOTER matching screenshot */}
  

      {/* PACKAGE SELECTION DIALOG */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold text-[#0D004C]">
          Choose Package for {selectedService?.name}
        </DialogTitle>

        <DialogContent dividers>
          <RadioGroup
            name="variant"
            value={selectedVariant}
            onChange={(e) => setSelectedVariant(e.target.value)}
          >
            {["none", "classic", "standard", "premium"]
              .filter(
                (variant) =>
                  variant === "none" || Number(variantPrices[variant]) > 0
              )
              .map((variant) => (
                <Box
                  key={variant}
                  sx={{
                    border: selectedVariant === variant ? "2px solid #0D004C" : "1px solid #ccc",
                    borderRadius: 2,
                    p: 2,
                    mb: 2,
                    backgroundColor: selectedVariant === variant ? "#f0f4ff" : "#fff",
                  }}
                >
                  <FormControlLabel
                    value={variant}
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle1" sx={{ textTransform: "capitalize", fontWeight: 600 }}>
                          {variant === "none" ? "No Package" : variant}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {variant === "none"
                            ? "Standard service without extra package add-on."
                            : variant === "classic"
                            ? selectedService?.classicDescription
                            : variant === "standard"
                            ? selectedService?.standardDescription
                            : selectedService?.primeDescription || "No description available"}
                        </Typography>
                        <Typography variant="body1" color="primary" sx={{ mt: 1 }}>
                          Price: ₹{variantPrices[variant] || 0}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              ))}
          </RadioGroup>

          {selectedVariant && (
            <Box mt={2}>
              <Typography variant="h6">Price Summary</Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Base Price</Typography>
                <Typography variant="body2">₹{selectedService?.price || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Typography variant="body2">Package Price</Typography>
                <Typography variant="body2">₹{variantPrices[selectedVariant] || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2} fontWeight="bold">
                <Typography variant="body1">Total</Typography>
                <Typography variant="body1">
                  ₹{Number(selectedService?.price || 0) + Number(variantPrices[selectedVariant] || 0)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setOpenDialog(false);
              setSelectedVariant("none");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#0D004C" }}
            disabled={!selectedVariant}
            onClick={() => {
              const totalPrice =
                Number(selectedService?.price || 0) + Number(variantPrices[selectedVariant] || 0);

              const itemToAdd = {
                ...selectedService,
                price: totalPrice,
                variant: selectedVariant,
              };

              handleIncrease(itemToAdd);
              setOpenDialog(false);
              setSelectedVariant("none");
            }}
          >
            Add Package
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ServiceDetails;
